import PropTypes from 'prop-types'
import React from 'react'
import { Link } from 'react-router-dom'
import { injectIntl } from 'react-intl'
import {
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap'
import BusyOverlay from 'components/BusyOverlay'
import RelativeDate from 'components/RelativeDate'
import ActivityContent from 'components/ActivityContent'
import KanBoIcon from 'components/KanBoIcon'
import ActivityUser from './ActivityUser'
import classes from './ActivityGroup.module.scss'
import { CardLink } from 'components/Link'

const DefaultBoardNameComponent = ({ boardId, children, className }) =>
  <Link
    to={'/board/' + boardId}
    className={className}
  >
    {children}
  </Link>

const DefaultCardNameComponent = ({ cardId, children, className }) =>
  <CardLink
    cardId={cardId}
    className={className}
  >
    {children}
  </CardLink>

export class ActivityGroup extends React.Component {
  static propTypes = {
    intl: PropTypes.object.isRequired,

    context: PropTypes.string.isRequired,
    timestamp: PropTypes.number.isRequired,
    currentUser: PropTypes.object.isRequired,
    isBusy: PropTypes.bool,
    userComponent: PropTypes.func.isRequired,
    contentComponent: PropTypes.func.isRequired,
    boardNameComponent: PropTypes.func.isRequired,
    cardNameComponent: PropTypes.func.isRequired,
    boardId: PropTypes.number,
    boardName: PropTypes.string,
    cardId: PropTypes.number,
    cardName: PropTypes.string,
    visitors: PropTypes.array,
    onBoardNameClick: PropTypes.func,
    onCardNameClick: PropTypes.func,

    activities: PropTypes.array.isRequired,

    fromUser: PropTypes.object.isRequired,
    activityCounts: PropTypes.array.isRequired
  }

  static defaultProps = {
    contentComponent: ActivityContent,
    boardNameComponent: DefaultBoardNameComponent,
    cardNameComponent: DefaultCardNameComponent,
    visitors: []
  }

  renderHiddenGroup = () => {
    const {
      activityCounts
    } = this.props

    return (
      <React.Fragment>
        {this.renderVisitors()}
        <div className={classes.hiddenGroup}>
          {activityCounts.map(activityCount =>
            <span
              key={activityCount.iconName}
              className={classes.hiddenCountTile}
            >
              {activityCount.count > 1 &&
                <span className={classes.hiddenCount}>
                  {activityCount.count}x
                </span>
              }
              <KanBoIcon
                name={activityCount.iconName}
                className={classes.hiddenIcon}
              />
            </span>
          )}
        </div>
      </React.Fragment>
    )
  }

  renderActivityGroup = () => {
    const {
      userComponent,
      boardId,
      boardName,
      cardId,
      cardName,
      boardNameComponent: BoardNameComponent,
      cardNameComponent: CardNameComponent,

      context,
      timestamp,
      isBusy,

      fromUser,
    } = this.props

    let showBoard = false
    let showCard = false

    if (
      !!boardId &&
      context === 'notifications'
    ) {
      showBoard = true
    }

    if (
      !!cardId && (
        context === 'notifications' ||
        context.substr(0, 6) === 'board-'
      )
    ) {
      showCard = true
    }

    return (
      <div className={classes.activityGroup}>
        <ActivityUser
          avatar
          defaultMenu
          presence
          size={40}
          fromUser={fromUser}
          userComponent={userComponent}
        />
        <div className={classes.contentWrapper}>
          {isBusy && <BusyOverlay />}
          <div
            className={classes.header}
          >
            <ActivityUser
              text
              defaultMenu
              fromUser={fromUser}
            />
            <span className={classes.date}>
              <RelativeDate pastOnly timestamp={timestamp} />
            </span>
            {this.renderVisitors()}
          </div>
          {(showBoard || showCard) &&
            <div className={classes.boardWrapper}>
              {showBoard &&
                <BoardNameComponent
                  boardId={boardId}
                  boardName={boardName}
                  className={classes.boardName}
                >
                  <KanBoIcon
                    name='kanbo-16-my-board'
                    className={classes.boardIcon}
                  />
                  {boardName}
                </BoardNameComponent>
              }
              {(showBoard && showCard) &&
                <span className={classes.boardDivider}>/</span>
              }
              {showCard &&
                <CardNameComponent
                  cardId={cardId}
                  cardName={cardName}
                  className={classes.boardName}
                >
                  <KanBoIcon
                    name='kanbo-16-card'
                    className={classes.boardIcon}
                  />
                  {cardName}
                </CardNameComponent>
              }
            </div>
          }
          <div className={classes.messageWrapper}>
            {this.renderActivities()}
          </div>
        </div>
      </div>
    )
  }

  renderVisitors = () => {
    const {
      intl,
      visitors,
      userComponent: UserComponent,
    } = this.props

    if (!visitors.length) return null

    let moreVisitors = false
    let visitorsToDisplay = visitors

    if (visitors.length > 10) {
      moreVisitors = visitors.length - 10
      visitorsToDisplay = visitors.slice(-10)
    }

    return (
      <div className={classes.visitors}>
        {moreVisitors &&
          <div className={`${classes.visitor} ${classes.moreVisitors}`}>
            +{moreVisitors}
          </div>
        }
        {visitorsToDisplay.map(({ id, timestamp, action }) => {
          const isOnline = action && action === 'Being'

          return (
            <RelativeDate
              key={id}
              pastOnly
              timestamp={timestamp || Date.now()} // Timestamp can be null
            >
              {({ text }) =>
                <OverlayTrigger
                  placement='top'
                  animation={false}
                  overlay={
                    <Tooltip>
                      {isOnline ? (
                        intl.formatMessage({ id: 'USER_PRESENCE_IS_IN_CARD' })
                      ) : (
                        text
                      )}
                    </Tooltip>
                  }
                >
                  <div
                    className={classes.visitor}
                  >
                    <UserComponent
                      className={isOnline ? classes.visitorOnline : undefined}
                      avatar
                      draggable
                      defaultMenu
                      id={id}
                      size={20}
                    />
                  </div>
                </OverlayTrigger>
              }
            </RelativeDate>
          )
        })}
      </div>
    )
  }

  renderActivities = () => {
    const {
      activityRef,

      activities,
      contentComponent: ContentComponent,
      focusedActivity,
      activityWasFocused,
      type,

      context,
      currentUser,

      fromUser
    } = this.props

    return activities.map(activity =>
      <ContentComponent
        {...activity}
        ref={activityRef ? ref => activityRef(ref, activity.id) : undefined}
        key={activity.id}
        groupType={type}
        fromUser={fromUser}
        context={context}
        currentUser={currentUser}
        flash={focusedActivity === activity.id && activityWasFocused}
      />
    )
  }

  render () {
    const {
      type
    } = this.props

    return type === 'hidden' ? (
      this.renderHiddenGroup()
    ) : (
      this.renderActivityGroup()
    )
  }
}

export default injectIntl(ActivityGroup, { forwardRef: true })
